import { useCallback, useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Download, Share2 } from "@/lib/icons";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { GardenPlant } from "@/components/client/Garden";

const W = 900, H = 1200;

function rounded(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

function plantImage(stage, progress) {
  const svg = renderToStaticMarkup(<GardenPlant stage={stage} progress={progress} animate={false} />).replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
  return new Promise((res) => { const img = new Image(); img.onload = () => res(img); img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`; });
}

export async function drawPostcard(canvas, game, wins) {
  const ctx = canvas.getContext("2d");
  canvas.width = W; canvas.height = H;
  ctx.fillStyle = "#f5efe4"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#e7dfd0"; for (let i = 0; i < 160; i++) ctx.fillRect((i * 97) % W, (i * 181) % H, 2, 2);
  ctx.strokeStyle = "#d6c8b2"; ctx.lineWidth = 3; rounded(ctx, 30, 30, W - 60, H - 60, 36); ctx.stroke();
  ctx.fillStyle = "#9a3412"; ctx.font = "bold 24px ui-sans-serif, system-ui"; ctx.letterSpacing = "6px"; ctx.textAlign = "center";
  ctx.fillText("A POSTCARD FROM MY GARDEN", W / 2, 110); ctx.letterSpacing = "0px";
  const img = await plantImage(game.level.index - 1, game.level.progress);
  ctx.drawImage(img, (W - 420) / 2, 150, 420, 560);
  ctx.fillStyle = "#1c1917"; ctx.font = "800 64px Georgia, ui-serif, serif";
  ctx.fillText(game.level.name, W / 2, 790);
  ctx.fillStyle = "#57534e"; ctx.font = "500 26px ui-sans-serif, system-ui";
  ctx.fillText("Grown one small check-in at a time", W / 2, 832);
  const stats = [[wins.checkins, "waterings"], [wins.active_days, "active days"], [wins.best_streak, "day streak"]];
  stats.forEach(([v, l], i) => {
    const x = 90 + i * 250, y = 890;
    ctx.fillStyle = "#ffffff"; rounded(ctx, x, y, 220, 150, 26); ctx.fill();
    ctx.fillStyle = "#b45309"; ctx.font = "800 60px ui-monospace, monospace"; ctx.fillText(String(v), x + 110, y + 82);
    ctx.fillStyle = "#57534e"; ctx.font = "600 22px ui-sans-serif, system-ui"; ctx.fillText(l, x + 110, y + 124);
  });
  ctx.fillStyle = "#78716c"; ctx.font = "500 22px ui-sans-serif, system-ui";
  ctx.fillText("this week · Root & Rose", W / 2, 1110);
  ctx.textAlign = "left";
}

export function GardenPostcard({ game }) {
  const [open, setOpen] = useState(false);
  const [wins, setWins] = useState(null);
  const canvasRef = useRef(null);
  useEffect(() => { if (open && !wins) api.get("/wins/weekly").then((r) => setWins(r.data)).catch(() => toast.error("Couldn't load this week's counts")); }, [open, wins]);
  useEffect(() => { if (open && wins && game && canvasRef.current) drawPostcard(canvasRef.current, game, wins); }, [open, wins, game]);
  const toBlob = useCallback(() => new Promise((res) => canvasRef.current.toBlob(res, "image/png")), []);
  const download = async () => {
    const blob = await toBlob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "garden-postcard.png"; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast.success("Saved. Just your plant and counts — nothing that says who you are.");
  };
  const share = async () => {
    const file = new File([await toBlob()], "garden-postcard.png", { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: "A postcard from my garden" }); } catch { /* cancelled */ }
    } else { await download(); }
  };
  if (!game) return null;
  return (
    <section className="warm-card p-5 sm:p-6" data-testid="garden-postcard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-800"><Mail className="w-3.5 h-3.5" /> Garden postcard</div>
          <p className="text-sm text-stone-600 mt-1">Send a friend a tiny picture of your plant and this week's counts. No name, no handle, no wellbeing — just the garden.</p>
        </div>
        <button data-testid="postcard-open-btn" onClick={() => setOpen((v) => !v)} className="text-xs font-semibold px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-amber-700">{open ? "Hide" : "Make a postcard"}</button>
      </div>
      {open && (
        <div className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,300px)_1fr] items-start">
          <canvas ref={canvasRef} data-testid="postcard-canvas" className="w-full rounded-2xl shadow-lg" style={{ aspectRatio: "3 / 4" }} />
          <div className="flex gap-2 flex-wrap">
            <button data-testid="postcard-download-btn" onClick={download} disabled={!wins} className="amber-btn text-xs py-2 inline-flex items-center gap-1.5 disabled:opacity-40"><Download className="w-3.5 h-3.5" /> Download PNG</button>
            <button data-testid="postcard-share-btn" onClick={share} disabled={!wins} className="text-xs font-semibold px-4 py-2 rounded-full bg-stone-100 text-stone-800 hover:bg-stone-200 inline-flex items-center gap-1.5 disabled:opacity-40"><Share2 className="w-3.5 h-3.5" /> Share</button>
          </div>
        </div>
      )}
    </section>
  );
}
